import { useEffect, useRef, useState } from "react";
import { useAddressSuggest, type AddressSuggestion } from "@/hooks/useAddressSuggest.js";

export interface AddressPick {
  label: string;
  lat: number;
  lng: number;
  displayName: string;
}

interface AddressAutocompleteProps {
  /** id für das innere Input — erlaubt <label htmlFor> in der Eltern-Komponente. */
  id?: string;
  value: string;
  /** Freitext-Eingabe (jeder Tastendruck). */
  onChange: (value: string) => void;
  /** Auswahl eines Vorschlags inkl. Koordinaten. */
  onSelect: (pick: AddressPick) => void;
  placeholder?: string;
  /** Optionaler Style-Override für das Input (sonst greift globales input-CSS). */
  inputStyle?: React.CSSProperties;
  "data-testid"?: string;
}

/**
 * Adress-Eingabe mit Autovervollständigung. Tippen liefert debounced Vorschläge
 * (Nominatim/AT via Backend-Proxy); Auswahl liefert Label + Koordinaten zurück,
 * sodass der Nutzer keine Geodaten mehr manuell suchen muss.
 */
export function AddressAutocomplete({
  id,
  value,
  onChange,
  onSelect,
  placeholder,
  inputStyle,
  "data-testid": dataTestId,
}: AddressAutocompleteProps) {
  const [open, setOpen] = useState(false);
  const [highlight, setHighlight] = useState(-1);
  // Nach einer Auswahl nicht sofort wieder Vorschläge für denselben Text laden.
  const [justPicked, setJustPicked] = useState(false);
  const { suggestions, loading } = useAddressSuggest(value, { enabled: open && !justPicked });
  const wrapRef = useRef<HTMLDivElement>(null);

  // Klick außerhalb schließt die Liste.
  useEffect(() => {
    function onDocMouseDown(e: MouseEvent) {
      if (wrapRef.current && !wrapRef.current.contains(e.target as Node)) {
        setOpen(false);
      }
    }
    document.addEventListener("mousedown", onDocMouseDown);
    return () => document.removeEventListener("mousedown", onDocMouseDown);
  }, []);

  // Highlight zurücksetzen, wenn sich die Vorschläge ändern.
  useEffect(() => {
    setHighlight(-1);
  }, [suggestions]);

  const pick = (s: AddressSuggestion) => {
    setJustPicked(true);
    onChange(s.label);
    onSelect({ label: s.label, lat: s.lat, lng: s.lng, displayName: s.displayName });
    setOpen(false);
    setHighlight(-1);
  };

  const handleKeyDown = (e: React.KeyboardEvent<HTMLInputElement>) => {
    if (!open || suggestions.length === 0) {
      if (e.key === "ArrowDown" && suggestions.length > 0) setOpen(true);
      return;
    }
    if (e.key === "ArrowDown") {
      e.preventDefault();
      setHighlight((h) => (h + 1) % suggestions.length);
    } else if (e.key === "ArrowUp") {
      e.preventDefault();
      setHighlight((h) => (h - 1 + suggestions.length) % suggestions.length);
    } else if (e.key === "Enter") {
      if (highlight >= 0 && highlight < suggestions.length) {
        e.preventDefault();
        pick(suggestions[highlight]!);
      }
    } else if (e.key === "Escape") {
      setOpen(false);
    }
  };

  const showDropdown = open && !justPicked && (loading || suggestions.length > 0);

  return (
    <div ref={wrapRef} style={{ position: "relative" }}>
      <input
        id={id}
        data-testid={dataTestId}
        type="text"
        value={value}
        autoComplete="off"
        placeholder={placeholder}
        style={inputStyle}
        onChange={(e) => {
          onChange(e.target.value);
          setJustPicked(false);
          setOpen(true);
        }}
        onFocus={() => {
          if (!justPicked) setOpen(true);
        }}
        onKeyDown={handleKeyDown}
        role="combobox"
        aria-expanded={showDropdown}
        aria-autocomplete="list"
      />

      {showDropdown && (
        <ul
          style={{
            listStyle: "none",
            margin: 0,
            padding: 4,
            position: "absolute",
            top: "calc(100% + 4px)",
            left: 0,
            right: 0,
            zIndex: 50,
            background: "var(--color-surface)",
            border: "1px solid var(--color-border)",
            borderRadius: "var(--radius)",
            boxShadow: "var(--shadow-md)",
            maxHeight: 260,
            overflowY: "auto",
          }}
        >
          {loading && suggestions.length === 0 && (
            <li style={{ padding: "8px 10px", fontSize: 13, color: "var(--color-text-muted)" }}>
              Suche Adressen…
            </li>
          )}
          {suggestions.map((s, i) => (
            <li key={`${s.lat},${s.lng},${i}`}>
              <button
                type="button"
                // mousedown statt click: feuert vor input-blur, damit die Auswahl nicht verloren geht.
                onMouseDown={(e) => {
                  e.preventDefault();
                  pick(s);
                }}
                onMouseEnter={() => setHighlight(i)}
                style={{
                  display: "block",
                  width: "100%",
                  textAlign: "left",
                  padding: "8px 10px",
                  borderRadius: 4,
                  fontSize: 13,
                  fontWeight: 400,
                  background: i === highlight ? "var(--color-bg)" : "transparent",
                  color: "var(--color-text)",
                }}
              >
                <span style={{ display: "block", fontWeight: 500 }}>{s.label}</span>
                <span style={{ display: "block", fontSize: 11, color: "var(--color-text-muted)" }}>
                  {s.displayName}
                </span>
              </button>
            </li>
          ))}
        </ul>
      )}
    </div>
  );
}
